import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/taskpane/contexts/auth/AuthContext';
import { waitForTokens } from '@/taskpane/services/sso';
import LoadingScreen from '@/taskpane/components/loading/LoadingScreen';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, Typography, CircularProgress, LinearProgress } from '@mui/material';

/**
 * SSO Polling View
 * ================
 * 
 * Used for cross-context SSO authentication (Desktop Outlook).
 * 
 * Flow:
 * 1. User clicks SSO button in Desktop Outlook add-in
 * 2. OAuth flow opens in external browser
 * 3. This view shows in add-in, polling backend for tokens
 * 4. Browser completes auth and stores tokens in backend
 * 5. This view retrieves tokens and completes login
 * 
 * Features:
 * - Countdown timer (5 minutes)
 * - Progress bar
 * - Cancellation button
 * - Automatic timeout handling
 */
export function SSOPollingView() {
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const [isPolling, setIsPolling] = useState(true);
  const { setTokens } = useContext(AuthContext);
  const navigate = useNavigate();
  const abortControllerRef = React.useRef<AbortController | null>(null);
  
  useEffect(() => {
    const sessionId = localStorage.getItem('sso_session_id');
    
    if (!sessionId) {
      console.log('[SSO Polling] No session ID found, redirecting to login');
      enqueueSnackbar('Session SSO non trouvée', { variant: 'warning' });
      navigate('/login');
      return;
    }
    
    console.log('[SSO Polling] Starting polling for session:', sessionId.substring(0, 10) + '...', {
      fullSessionId: sessionId, // Log full session ID for debugging
      maxDuration: '5 minutes',
      pollInterval: '5 seconds',
      platform: 'Desktop Outlook',
      endpoint: `/auth/sso/poll-tokens/${sessionId}`
    });
    
    // Create AbortController for this polling session
    abortControllerRef.current = new AbortController();
    
    // Start polling
    const startPolling = async () => {
      try {
        const tokens = await waitForTokens(
          sessionId,
          60, // maxAttempts
          5000, // interval
          abortControllerRef.current?.signal // Pass abort signal
        );
        
        // Success!
        console.log('[SSO Polling] ✓ Tokens received! Storing in AuthContext and redirecting...');
        setIsPolling(false);
        setTokens(tokens);
        localStorage.removeItem('sso_session_id');
        enqueueSnackbar('Connexion SSO réussie', { variant: 'success' });
        navigate('/folders', { replace: true });
        
      } catch (error) {
        // Check if it was a cancellation
        if (error instanceof Error && error.message === 'Polling operation was cancelled') {
          console.log('[SSO Polling] Polling was cancelled by user or component unmount');
          return; // Don't show error message for intentional cancellation
        }
        
        console.error('[SSO Polling] ✗ Polling failed:', {
          error,
          message: error instanceof Error ? error.message : 'Unknown error',
          sessionId: sessionId.substring(0, 10) + '...',
          duration: '5 minutes (timeout)'
        });
        setIsPolling(false);
        enqueueSnackbar(
          'Délai d\'attente dépassé. Veuillez réessayer.',
          { variant: 'error' }
        );
        localStorage.removeItem('sso_session_id');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    };
    
    startPolling();
    
    // Countdown timer
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Cleanup function: abort polling when component unmounts
    return () => {
      console.log('[SSO Polling] Component unmounting, aborting polling...');
      clearInterval(timer);
      abortControllerRef.current?.abort();
    };
  }, [setTokens, navigate]);
  
  const handleCancel = () => {
    console.log('[SSO Polling] User cancelled polling');
    
    // Abort the polling operation
    abortControllerRef.current?.abort();
    
    localStorage.removeItem('sso_session_id');
    setIsPolling(false);
    enqueueSnackbar('Authentification SSO annulée', { variant: 'info' });
    navigate('/login', { replace: true });
  };
  
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const progressPercent = ((300 - timeRemaining) / 300) * 100;
  
  return (
    <Box 
      sx={{ 
        p: 4, 
        textAlign: 'center', 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'background.default'
      }}
    >
      {/* Loading spinner */}
      <CircularProgress size={60} sx={{ mb: 3 }} />
      
      {/* Main message */}
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
        Connexion SSO en cours...
      </Typography>
      
      {/* Instructions */}
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Veuillez compléter l'authentification dans votre navigateur
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontStyle: 'italic' }}>
        Une fenêtre de connexion Septeo devrait s'ouvrir dans votre navigateur par défaut
      </Typography>
      
      {/* Progress bar */}
      <Box sx={{ width: '100%', maxWidth: 400, mb: 2 }}>
        <LinearProgress 
          variant="determinate" 
          value={progressPercent} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>
      
      {/* Countdown timer */}
      <Typography 
        variant="h6" 
        color={timeRemaining < 60 ? 'error.main' : 'text.secondary'} 
        sx={{ mb: 4, fontWeight: 500 }}
      >
        Temps restant: {formatTime(timeRemaining)}
      </Typography>
      
      {/* Cancel button */}
      <Button
        variant="outlined"
        color="error"
        onClick={handleCancel}
        disabled={!isPolling}
        sx={{ mt: 2 }}
      >
        Annuler
      </Button>
      
      {/* Help text */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 4, maxWidth: 400 }}>
        Si la fenêtre de connexion ne s'ouvre pas automatiquement, vérifiez que votre navigateur 
        n'a pas bloqué les pop-ups.
      </Typography>
    </Box>
  );
}

export default SSOPollingView;
